import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { AuthService } from './auth.service';

/**
 * Thin wrapper around a single dashboard socket.io connection to the
 * ChatGateway. Reused across dashboard components so a chatbot/conversation
 * room join in one component isn't torn down by another.
 */
@Injectable({ providedIn: 'root' })
export class ChatSocketService implements OnDestroy {
  private socket: Socket | null = null;
  /** Last chatbot/conversation rooms joined — re-emitted on every 'connect'
   *  (fires on the first connection AND any auto-reconnect). Socket.io rooms
   *  are per-connection: a dropped-then-restored connection gets a fresh
   *  socket.id in no rooms at all, so without this, a reconnect after a blip
   *  silently stopped delivering new-message/status-change/conversation-
   *  updated broadcasts for whatever the dashboard had open — no error, no
   *  visible sign, just missing messages until a manual page reload. */
  private joinedChatbotId: string | null = null;
  private joinedConversationId: string | null = null;

  constructor(private readonly auth: AuthService) {}

  connect(): Socket {
    if (this.socket) return this.socket;

    this.socket = io('/chat', {
      auth: { token: this.auth.getToken() },
      transports: ['websocket', 'polling'],
    });
    this.socket.on('connect', () => {
      if (this.joinedChatbotId) this.socket?.emit('join-chatbot', { chatbotId: this.joinedChatbotId });
      if (this.joinedConversationId) this.socket?.emit('join-conversation', { conversationId: this.joinedConversationId });
    });
    return this.socket;
  }

  joinChatbot(chatbotId: string): void {
    this.joinedChatbotId = chatbotId;
    this.connect().emit('join-chatbot', { chatbotId });
  }

  joinConversation(conversationId: string): void {
    this.joinedConversationId = conversationId;
    this.connect().emit('join-conversation', { conversationId });
  }

  on<T = unknown>(event: string, callback: (data: T) => void): void {
    this.connect().on(event, callback as (...args: unknown[]) => void);
  }

  off<T = unknown>(event: string, callback: (data: T) => void): void {
    this.socket?.off(event, callback as (...args: unknown[]) => void);
  }

  /**
   * Tears down the current socket so the next connect() call opens a fresh
   * one authenticated with whatever token is current at that point. Without
   * this, connect()'s `if (this.socket) return this.socket` short-circuit
   * means a socket opened under one user's JWT keeps being reused — across
   * a logout and a different user logging into the same tab — so a stale
   * connection stays authenticated as the previous user. Called from
   * AuthService on both logout and a fresh login.
   */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.joinedChatbotId = null;
    this.joinedConversationId = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
